<script lang="ts">
  import { onMount } from 'svelte';
  import { link, push } from 'svelte-spa-router';
  import { location } from '../../framework/js/spaLocation';
  import { api } from '../../framework/js/api.js';
  import GapfinderView from './GapfinderView.svelte';
  import GapfinderList from './GapfinderList.svelte';
  import GapfinderCreator from './GapfinderCreator.svelte';
  import FlowModal from '../../framework/components/FlowModal.svelte';
  import { AppPageHeader, AgentHero } from '../../framework/components/app';

  let analysisId: string | null = null;
  let isNew = false;
  let hasItems: boolean | null = null;
  let totalCount: number | null = null;

  $: {
    const path = $location;
    if (path === '/gapfinder/new') {
      analysisId = null;
      isNew = true;
    } else if (path === '/gapfinder/history') {
      analysisId = null;
      isNew = false;
    } else {
      const match = path.match(/\/gapfinder\/([^/]+)/);
      if (match && match[1]) {
        analysisId = match[1];
        isNew = false;
      } else {
        analysisId = null;
        isNew = false;
      }
    }
  }

  async function probe() {
    try {
      const res: any = await api.getGapfinderAnalyses();
      if (res?.success && res?.data?.list) {
        const count = res.data.list.length;
        totalCount = count;
        hasItems = count > 0;
      } else {
        hasItems = false;
      }
    } catch {
      hasItems = false;
    }
  }

  function handleModalClose() {
    // Return to the list when the user dismisses the creator popup.
    push(hasItems ? '/gapfinder' : '/gapfinder');
  }

  onMount(() => {
    if (!analysisId) probe();
  });
</script>

<div class="gapfinder-page">
  {#if analysisId}
    {#key analysisId}
      <GapfinderView analysisId={analysisId} />
    {/key}
  {:else if hasItems === null}
    <!-- probing -->
  {:else if hasItems}
    <AppPageHeader
      title="GapFinder"
      subtitle="Review briefs you've previously evaluated."
      actionLabel="New analysis"
      actionHref="/gapfinder/new"
    />
    <GapfinderList />
  {:else}
    <AgentHero
      title="Evaluate your research brief"
      subtitle="Upload your market research brief for an instant, objective evaluation. We'll identify gaps, score alignment, and recommend the right tech platforms."
      features={[
        'Objective scoring across key research dimensions',
        'Actionable recommendations to improve clarity',
        'Curated shortlist of research tech platforms',
      ]}
      historyHref="/gapfinder"
      historyLabel="View past analyses"
      historyCount={totalCount}
    >
      <div slot="creator" class="creator-card">
        <h2 class="creator-title">Evaluate your first brief</h2>
        <p class="creator-sub">Upload your brief. We'll take you through the rest.</p>
        <a use:link href="/gapfinder/new" class="creator-cta">
          <span>Analyse brief</span>
          <i class="angle right icon" aria-hidden="true"></i>
        </a>
      </div>
    </AgentHero>
  {/if}
</div>

{#if isNew}
  <FlowModal on:close={handleModalClose}>
    <GapfinderCreator wizard={true} />
  </FlowModal>
{/if}

<style>
  .gapfinder-page {
    padding: var(--space-5) var(--space-6);
  }
  @media (max-width: 768px) {
    .gapfinder-page { padding: var(--space-4); }
  }

  .creator-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-pop);
    padding: var(--space-6);
    text-align: center;
  }

  .creator-title {
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
  }

  .creator-sub {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  .creator-cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-6);
    background: var(--primary);
    color: var(--white);
    border-radius: var(--radius-cta);
    text-decoration: none;
    font-weight: 500;
    box-shadow: var(--shadow-cta);
    transition: filter .15s;
  }

  .creator-cta:hover { filter: brightness(.95); color: var(--white); }
  .creator-cta:focus-visible { outline: none; box-shadow: var(--focus-ring); }
</style>
